import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { PanelBarTemplate } from '../../../../../shared/expandable-panelbar/expandable-panelbar.component';
import { PanelbarService } from "../../../../../shared/expandable-panelbar/expandable-panelbar.service";
import { RequestDetailService } from '../detail.service';
import { ApprovalStatus } from '../../../workflow.service';
import { Injector } from '@angular/core';
import { UserDelegateTemplate } from '../delegate.template';
import { DatePipe } from '@angular/common';
import { CtwrRequestService } from '../../../ctwr.service';


export class LocationApprovalPanelbar extends PanelBarTemplate {

    private readonly formBuilder: FormBuilder;
    private readonly panelbarService: PanelbarService;
    private readonly detailService: RequestDetailService;
    private readonly userDelegateTemplate: UserDelegateTemplate;
    private readonly requestService: CtwrRequestService;
    private requestDetail: any;

    constructor(
        private readonly injector: Injector,
    ) {
        super();
        this.formBuilder = injector.get(FormBuilder);
        this.panelbarService = injector.get(PanelbarService);
        this.detailService = injector.get(RequestDetailService);
        this.requestService = this.injector.get(CtwrRequestService);
        this.title = 'Location Approvals';
        this.id = this.title;

        this.iconStack = false;
        this.userDelegateTemplate = new UserDelegateTemplate(this.injector);

        this.detailService.requestDetail
            .subscribe(x => this.requestDetail = x);
    }


    /**
     * 
     * @param dataItem 
     */
    setLocationApprovalData(dataItem: any) {
        this.reset();
        if (dataItem.LocationApprovals) {
            dataItem.LocationApprovals.forEach(la => {
                this.addPanelBar(this.getLocationApprovalPanelbar(la));
            })
        }
        else if (dataItem.RequestId) {
            const _form: FormGroup = this.getEditForm(dataItem);
            this.detailService.addTask(dataItem.WFTaskId, _form, this);
            this.forms.push(_form);
        }
    }


    /**
     * Refresh Part Approval Matrix
     * If all locations have been approved/rejected, refresh Work Approval Matrix
     */
    refresh = () => {
        this.loading.next(true);
        this.detailService.refreshRequest();
        this.detailService.refreshPartApprovalMatrix()
            .then(x => this.loading.next(false))
            .catch(x => this.loading.next(false));
    }


    /**
    * Initialize and return Location Approval Panelbar instance
    * @param la - Location Approval
    */
    private getLocationApprovalPanelbar(la: any): LocationApprovalPanelbar {
        const pb = new LocationApprovalPanelbar(this.injector);

        pb.approvalStatus.next(la.Status);

        const _title: string = la.Title;
        pb.title = _title;
        pb.id = la.WFTaskId;
        pb.iconStack = false;
        pb.iconClass = 'fa-map-marker secondary-purple';
        pb.setLocationApprovalData(la);
        return pb;
    }


    /**
     * 
     * @param dataItem 
     */
    private getEditForm(dataItem: any): FormGroup {
        const form = this.formBuilder.group(this.LocationApprovalEditForm);
        dataItem.ApprovalDate = new DatePipe('en-US')
            .transform(dataItem.ApprovalDate, 'short');
        form.reset(dataItem);
        this.updateFormValidation(form);
        return form;
    }


    /**
     * 
     * @param editForm 
     * @param isEnable 
     */
    private updateFormValidation(editForm: FormGroup, isEnable = false) {
        const isFormEnabled: boolean = editForm.get('Enabled').value;
        const isPending: boolean = editForm.get('Status').value === ApprovalStatus.PENDING;

        editForm.get('Priority').disable();
        editForm.get('IsApproved').disable();
        editForm.removeControl('RejectComment');

        if (isFormEnabled && isPending) {
            editForm.get('IsApproved').enable();
            if (editForm.get('IsApproved').value) {
                editForm.get('Priority').enable();
            }
        }
    }


    /**
     * 
     */
    private readonly handleApproveChange = (control: any, form: FormGroup) => {
        const ctrl = form.get(control.name);
        if (ctrl.value) {
            form.removeControl('RejectComment');
            form.get('Priority').enable();
        }
        else {
            form.get('Priority').disable();
            form.addControl('RejectComment', new FormControl('', Validators.required));
        }
    }


    /**
     * 
     */
    private readonly handleSubmit = (form: FormGroup) => {
        const dataItem = form.value;
        const _status = dataItem.IsApproved ? ApprovalStatus.APPROVED : ApprovalStatus.REJECTED;
        const _dto: any = {
            Status: _status,
            Response: JSON.stringify({
                Priority: dataItem.IsApproved ? dataItem.Priority : 0,
                RejectComment: dataItem.RejectComment
            })
        };


        this.loading.next(true);
        this.requestService.save(_dto, false, `/WFTask/UpdateTaskStatus?id=${dataItem.WFTaskId}`, false)
            .then(() => {
                this.loading.next(false);
                this.refresh();
            })
            .catch(e => {
                this.requestService.handleError(e);
                this.loading.next(false);
            });


        form.get('IsApproved').disable();
        form.get('Priority').disable();
        form.get('submitBtn').disable();
    }


    /**
     * 
     */
    private readonly isSubmitDisabled = (c, f): boolean => {
        return !f.get('submitBtn').enabled ||
            !f.value.Enabled ||
            !f.valid ||
            f.value.Status === ApprovalStatus.APPROVED;
    }

    /**
     * 
     */
    private readonly handleUserDelegateSave = (dataItem, form) => {
        this.loading.next(true);
        form.get('Enabled').setValue(false);
        this.updateFormValidation(form);
        this.requestService.save({}, false, `/WFTask/Delegate?id=${dataItem.WFTaskId}&assignedTo=${dataItem.Email}`, false)
            .then(() => {
                this.loading.next(false);
                this.refresh();
            })
            .catch(e => {
                this.requestService.handleError(e);
                this.loading.next(false);
            });

    }


    /**
     * 
     */
    private readonly handleDelegateUser = (form: any = null, control: any = null) => {
        if (!form && !control) return this.detailService.isCtwrAdmin;
        this.panelbarService.openDialog(this.userDelegateTemplate);
        this.userDelegateTemplate.refreshUsers('TM');
        this.userDelegateTemplate.next({ WFTaskId: form.value.WFTaskId, Email: form.value.TMEmail });
        this.userDelegateTemplate.save = ({ dataItem }) => {
            this.handleUserDelegateSave(dataItem, form);
        }
        return null;
    }


    /**
        * 
        */
    isDelegateDisabled = (form) => {
        return this.loading.value ||
            form.value['Status'] !== ApprovalStatus.PENDING ||
            this.requestDetail.RequestStatus === ApprovalStatus.REWORK_REQUESTED ||
            this.requestDetail.RequestStatus === ApprovalStatus.CANCELLED;
    }


    /**
     * 
     */
    readonly isOptionDisabled = (form, isReview = false): any => {
        try {
            const data = form.value;
            if (!data.Enabled || data.Status !== ApprovalStatus.PENDING || (!isReview && !form.value.IsApproved)) {
                return true;
            }
            else {
                return null;
            }
        } catch (e) {
            return false;
        }
    }

    controls = [
        {
            type: 'label',
            title: 'Status',
            name: 'Status',
        },
        {
            type: 'radio',
            inline: true,
            title: 'Review',
            name: 'IsApproved',
            change: this.handleApproveChange,
            options: [
                {
                    value: true,
                    label: 'Approve',
                    disabled: (form) => this.isOptionDisabled(form, true),
                },
                {
                    value: false,
                    label: 'Reject',
                    disabled: (form) => this.isOptionDisabled(form, true),
                },
            ]
        },
        {
            type: 'user',
            name: 'TMName',
            title: 'TM',
            emailField: 'TMEmail',
            delegate: this.handleDelegateUser,
            disabled: this.isDelegateDisabled
        },
        {
            type: 'radio',
            inline: true,
            text: 'Priority',
            title: 'Priority',
            name: 'Priority',
            enabled: false,
            options: [
                {
                    value: 0,
                    label: 'Normal',
                    disabled: this.isOptionDisabled,
                },
                {
                    value: 1,
                    label: 'Urgent',
                    disabled: this.isOptionDisabled,
                },
            ]
        },
        {
            type: 'textarea',
            title: 'Reject comments',
            name: 'RejectComment',
        },
        {
            type: 'label',
            name: 'ApprovalDate',
            title: 'Approval Date',
        },
        {
            type: 'submit',
            primary: true,
            title: 'Submit',
            text: 'Submit',
            name: 'submitBtn',
            align: 'right',
            disabled: this.isSubmitDisabled,
            click: this.handleSubmit
        },
    ]


    LocationApprovalEditForm = {
        RequestId: [],
        Enabled: [],
        TMName: [],
        TMEmail: [],
        TMApprovedId: [],
        ApprovalDate: [],
        Priority: [],
        Status: [],
        IsApproved: [null, Validators.required],
        WFTaskId: [],
        submitBtn: [],
    }
}
